<script lang="ts">
  import { faPlus } from '@itdev-fortawesome/pro-solid-svg-icons';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import type { PageProps } from './$types';
  import type { SkillCategory, Skill } from '$lib/api/types';
  import SkillCategoryEditor from '$lib/components/courses/SkillCategoryEditor.svelte';
  import SkillEditor from '$lib/components/courses/SkillEditor.svelte';
  import SkillBtn from '$lib/components/common/SkillBtn.svelte';

  let { params, data }: PageProps = $props();

  let courseId = $derived.by(() => {
    return parseInt(params.id);
  });

  type ModalAction = 'category' | 'skill' | null;
  let modalAction: ModalAction = $state(null);
  let editingCategory: SkillCategory | null = $state(null);
  let editingSkill: Skill | null = $state(null);
  let newSkillCategoryId: number | null = $state(null);
  function openModal(action: ModalAction) {
    modalAction = action;
  }

  function closeModal() {
    modalAction = null;
    editingSkill = null;
    editingCategory = null;
    newSkillCategoryId = null;
  }
</script>

<div class="d-flex justify-content-end mb-4">
  <button type="button" class="btn btn-primary" onclick={() => openModal('category')}>New Category</button>
</div>

{#await data.coursePromise then course}
  {#if course.skillCategories.length === 0}
    <p class="text-center text-secondary p-3 fst-italic">
      This course has no skills. Click the "New Category" button above to create a category in order to add skills for
      this course.
    </p>
  {/if}
  <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
    {#each course.skillCategories as cat (cat.id)}
      {@render catSnippet(cat)}
    {/each}
  </div>
{/await}

{#if modalAction === 'category'}
  <SkillCategoryEditor {courseId} skillCategory={editingCategory} onClose={closeModal} />
{:else if modalAction === 'skill'}
  <SkillEditor
    {courseId}
    categoryId={newSkillCategoryId ?? editingSkill!.id}
    skill={editingSkill}
    onClose={closeModal}
  />
{/if}

{#snippet catSnippet(cat: SkillCategory)}
  <div class="col d-grid">
    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="text-center mb-3">
          <img src={cat.badge} alt="Badge for {cat.name}" class="category-badge" />
        </div>
        <div class="d-flex align-items-end">
          <h3 class="skill-category-header">
            {cat.name} (<button
              type="button"
              class="btn btn-link p-0 fw-medium"
              onclick={() => {
                openModal('category');
                editingCategory = cat;
              }}>edit</button
            >)
          </h3>
          <button
            class="btn add-skill"
            aria-label="Add Skill"
            onclick={() => {
              newSkillCategoryId = cat.id;
              openModal('skill');
            }}><FontAwesomeIcon icon={faPlus} /></button
          >
        </div>
        {#each cat.skills as skill (skill.id)}
          <SkillBtn
            name={skill.name}
            pointsValue={skill.pointsValue}
            onclick={() => {
              editingSkill = skill;
              openModal('skill');
            }}
          />
        {/each}
        {#if cat.skills.length === 0}
          <p class="text-center text-secondary fst-italic">No skills</p>
        {/if}
      </div>
    </div>
  </div>
{/snippet}

<style>
  .category-badge {
    height: 100px;
    width: auto;
  }
  .skill-category-header {
    font-size: 1em;
    margin-bottom: 0;
    flex: 1;
  }

  .add-skill {
    font-size: 1.125em;
    padding: 0;
    color: var(--uams-primary-red);
  }

  .add-skill:hover {
    color: var(--uams-primary-red-hover);
  }
</style>
